import { Fragment, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { fetchCampaigns, type CampaignView } from '../api/campaigns';
import {
  deleteMission,
  fetchDeletedMissions,
  fetchMissions,
  fetchRecipesFor,
  restoreMission,
  type MissionCard,
  type RecipeView,
} from '../api/missions';
import { ca } from '../i18n/ca';
import { Avatar } from './IdentityPanel';
import { useAuth } from './AuthContext';
import { MissionCover } from './MissionCover';
import { PlannedButton, PlannedChip } from './Planned';
import { PageProblem } from './PageStates';
import { useRetryKey } from './connectionHelpers';
import { Shell } from './Shell';
import { useConfirm } from './useConfirm';

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * «Nova» is a mission made in the last seven days that nobody has played yet. It first meant
 * "made in the last week", which marked every card; then "since your last visit", which a
 * reader who came back after a month saw on everything and one who came daily never saw; a
 * date and a played session are facts of the mission, the same for every reader.
 */
function isNew(m: MissionCard, now: number): boolean {
  const t = Date.parse(m.createdAt);
  return !Number.isNaN(t) && now - t < 7 * DAY_MS && m.sessions.length === 0;
}

/** What a page shows for a mission's terrain; the world class is the identifier, not a name. */
function worldLabel(m: MissionCard): string {
  return m.worldDisplayName ?? m.worldName;
}

/** What a page shows for a mission's type; the recipe id is the identifier, not a name. */
function typeLabel(m: MissionCard): string | null {
  return m.recipeTitle ?? m.recipe;
}

/** The library (UI doc 5.6): mine, shared with me, public, plus campaigns and their templates. */
export function Library() {
  const { me } = useAuth();
  const [missions, setMissions] = useState<MissionCard[] | null>(null);
  const [campaigns, setCampaigns] = useState<CampaignView[] | null>(null);
  const [bin, setBin] = useState<MissionCard[]>([]);
  const [tab, setTab] = useState<'missions' | 'campaigns' | 'bin'>('missions');
  const [filter, setFilter] = useState<'all' | 'mine' | 'shared' | 'public'>('all');
  const [query, setQuery] = useState('');
  const [terrain, setTerrain] = useState('');
  const [players, setPlayers] = useState('');
  const [recipe, setRecipe] = useState('');
  const [duration, setDuration] = useState('');
  // A mission's typical length is its type's (Recipe.minutes); the cards do not carry it.
  const [recipeMinutes, setRecipeMinutes] = useState<Record<string, number>>({});
  // The server a mission was made for, by the name it had then: the panel may no longer list it.
  const [madeFor, setMadeFor] = useState('');
  const [notPlayed, setNotPlayed] = useState(false);
  const [sort, setSort] = useState<'recent' | 'mostPlayed'>('recent');
  // Read once per visit to the page, so a card does not change its badge while it is looked at.
  const [now] = useState(() => Date.now());
  const [filtersOpen, setFiltersOpen] = useState(false);
  // An admin's clean-up, as on the sessions list: many at once, and for good.
  const admin = me.roles.includes('ADMIN');
  const [confirm, confirmDialog] = useConfirm();
  const [selecting, setSelecting] = useState(false);
  const [picked, setPicked] = useState<Set<string>>(new Set());
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  const [failures, setFailures] = useState(0);
  /** The missions or the campaigns did not arrive: said so, never drawn as an empty shelf. */
  const [loadError, setLoadError] = useState<unknown>(null);
  const [attempt, retry] = useRetryKey();

  // The tests (a smoke night's mission, an inspection tour's, a guided test's)
  // stay out of the library until an admin asks for them (the owner, 2026-09-29).
  const [tests, setTests] = useState(false);
  const withTests = tests && me.roles.includes('ADMIN');

  // Independent calls, each rendering as soon as it lands.
  useEffect(() => {
    if (!me.authenticated) return;
    setLoadError(null);
    fetchMissions(withTests)
      .then(setMissions)
      .catch((err) => {
        setLoadError(err);
        setMissions([]);
      });
  }, [me.authenticated, withTests, attempt]);
  useEffect(() => {
    if (!me.authenticated) return;
    fetchCampaigns()
      .then(setCampaigns)
      .catch((err) => {
        setLoadError(err);
        setCampaigns([]);
      });
  }, [me.authenticated, attempt]);
  const editor = me.roles.includes('EDITOR') || me.roles.includes('ADMIN');

  useEffect(() => {
    if (tab !== 'bin' || !editor) return;
    fetchDeletedMissions()
      .then(setBin)
      .catch(() => setBin([]));
  }, [tab, editor]);

  const loaded = missions !== null;
  const all = useMemo(() => missions ?? [], [missions]);
  const q = query.trim().toLowerCase();

  // The terrain filter keeps the world class as its value (missions carry it) and shows the display name.
  const terrains = useMemo(
    () =>
      [...new Map(all.map((m) => [m.worldName, worldLabel(m)] as const))].sort((a, b) =>
        a[1].localeCompare(b[1]),
      ),
    [all],
  );
  // The filter keeps the recipe id as its value (missions carry it) and shows the name.
  const recipes = useMemo(
    () =>
      [
        ...new Map(
          all
            .filter((m) => m.recipe)
            .map((m) => [m.recipe as string, typeLabel(m) ?? m.recipe] as const),
        ),
      ].sort((a, b) => String(a[1]).localeCompare(String(b[1]))),
    [all],
  );
  const madeForNames = useMemo(
    () => [...new Set(all.map((m) => m.serverName).filter((n): n is string => !!n))].sort(),
    [all],
  );

  useEffect(() => {
    fetchRecipesFor()
      .then((rs: RecipeView[]) =>
        setRecipeMinutes(Object.fromEntries(rs.map((r) => [r.id, r.minutes] as const))),
      )
      .catch(() => setRecipeMinutes({}));
  }, []);

  const durationBucket = (m: MissionCard) => {
    const minutes = m.recipe ? recipeMinutes[m.recipe] : undefined;
    if (minutes === undefined) return null;
    return minutes <= 60 ? 'short' : minutes <= 120 ? 'medium' : 'long';
  };

  const playerBucket = (slots: number) =>
    slots <= 6 ? 'small' : slots <= 12 ? 'medium' : slots <= 24 ? 'large' : 'huge';

  const shown = all
    .filter((m) => {
      if (filter === 'mine' && m.ownerId !== me.id) return false;
      if (filter === 'shared' && (m.ownerId === me.id || !m.editors.includes(me.id ?? -1)))
        return false;
      if (filter === 'public' && m.visibility !== 'PUBLIC') return false;
      if (terrain && m.worldName !== terrain) return false;
      if (recipe && m.recipe !== recipe) return false;
      if (madeFor && (m.serverName ?? '') !== madeFor) return false;
      if (players && playerBucket(m.playerSlots) !== players) return false;
      if (duration && durationBucket(m) !== duration) return false;
      if (notPlayed && m.sessions.length > 0) return false;
      return (
        !q ||
        m.title.toLowerCase().includes(q) ||
        m.worldName.toLowerCase().includes(q) ||
        worldLabel(m).toLowerCase().includes(q) ||
        (m.recipe ?? '').toLowerCase().includes(q) ||
        (typeLabel(m) ?? '').toLowerCase().includes(q)
      );
    })
    .sort((a, b) =>
      sort === 'mostPlayed'
        ? b.sessions.length - a.sessions.length
        : b.createdAt.localeCompare(a.createdAt),
    );

  const lib = ca.missionUi.library;
  const activeFilters =
    [terrain, players, duration, recipe, madeFor].filter(Boolean).length + (notPlayed ? 1 : 0);
  const pickedCodes = shown.filter((m) => picked.has(m.code)).map((m) => m.code);

  async function purgeMany(codes: string[]) {
    const yes = await confirm({
      title: lib.purgeManyTitle(codes.length),
      body: lib.purgeManyBody,
      confirmLabel: lib.purgeMany(codes.length),
      danger: true,
    });
    if (!yes) return;
    let failed = 0;
    const gone = new Set<string>();
    setFailures(0);
    setProgress({ done: 0, total: codes.length });
    for (const [i, code] of codes.entries()) {
      try {
        await deleteMission(code, true);
        gone.add(code);
      } catch {
        failed += 1;
      }
      setProgress({ done: i + 1, total: codes.length });
    }
    setMissions((old) => (old ? old.filter((m) => !gone.has(m.code)) : old));
    setPicked(new Set());
    setProgress(null);
    setFailures(failed);
  }

  return (
    <Shell
      title={ca.library}
      actions={
        <>
          <PlannedButton id="library-import">{ca.missionUi.library.importMission}</PlannedButton>
          {editor && tab === 'missions' && (
            <Link className="button primary" to="/new">
              {ca.newMission}
            </Link>
          )}
          {editor && tab === 'campaigns' && (
            <Link className="button primary" to="/campaigns/new">
              {ca.newCampaign}
            </Link>
          )}
        </>
      }
    >
      <nav className="tabs" role="tablist">
        {(editor
          ? (['missions', 'campaigns', 'bin'] as const)
          : (['missions', 'campaigns'] as const)
        ).map((t) => (
          <Fragment key={t}>
            <button
              type="button"
              role="tab"
              aria-selected={tab === t}
              className={tab === t ? 'active' : ''}
              onClick={() => setTab(t)}
            >
              {t === 'bin' ? ca.missionUi.library.bin : ca.libraryTabs[t]}{' '}
              <span className="chip num">
                {t === 'missions'
                  ? (missions?.length ?? '·')
                  : t === 'campaigns'
                    ? (campaigns?.length ?? '·')
                    : bin.length}
              </span>
            </button>
            {/* Beside the tab, not inside it: a link cannot live inside a button. */}
            {t === 'campaigns' && <PlannedChip id="library-campaigns" />}
          </Fragment>
        ))}
      </nav>
      {tab === 'bin' && bin.length === 0 && (
        <p className="muted">{ca.missionUi.library.binEmpty}</p>
      )}
      {tab === 'bin' && (
        <section className="cards grid">
          {bin.map((m) => (
            <article className="card mission" key={m.code}>
              <div className="body">
                <strong>{m.title}</strong>
                <span className="muted small">
                  {worldLabel(m)} · {ca.missionUi.library.deletedOn(m.updatedAt.slice(0, 10))}
                </span>
                <button
                  type="button"
                  onClick={() =>
                    void restoreMission(m.code).then(() => {
                      setBin((list) => list.filter((x) => x.code !== m.code));
                      fetchMissions(withTests)
                        .then(setMissions)
                        .catch(() => undefined);
                    })
                  }
                >
                  {ca.missionUi.library.restore}
                </button>
                {admin && (
                  <button
                    type="button"
                    className="button small danger"
                    aria-label={lib.purgeOneLabel(m.title)}
                    onClick={() =>
                      void confirm({
                        title: lib.purgeOneTitle(m.title),
                        body: lib.purgeOneBody,
                        confirmLabel: lib.purgeOne,
                        danger: true,
                      }).then((yes) => {
                        if (!yes) return;
                        deleteMission(m.code, true)
                          .then(() => setBin((list) => list.filter((x) => x.code !== m.code)))
                          .catch(() => setFailures(1));
                      })
                    }
                  >
                    <i className="bi bi-trash" aria-hidden="true" />
                    {lib.purgeOne}
                  </button>
                )}
              </div>
            </article>
          ))}
        </section>
      )}
      {tab === 'campaigns' && campaigns === null && (
        <p className="muted" role="status">
          {ca.loading}
        </p>
      )}
      {tab === 'campaigns' && (
        <p className="notice" role="note" aria-label={ca.campaignsSoon.title}>
          <i className="bi bi-info-circle" aria-hidden="true" />{' '}
          <strong>{ca.campaignsSoon.title}</strong> {ca.campaignsSoon.text}
        </p>
      )}
      {tab === 'campaigns' && campaigns?.length === 0 && (
        <p className="muted">{ca.campaignsEmpty}</p>
      )}
      {tab === 'campaigns' && (
        <section className="cards grid">
          {(campaigns ?? []).map((c) => (
            <Link className="card" key={c.code} to={`/c/${c.code}`} aria-label={c.title}>
              <strong>{c.title}</strong>
              <span className="muted small">
                {c.worldDisplayName ?? c.worldName} · {ca.chapter(c.chapter)} ·{' '}
                {c.state === 'FINISHED' ? ca.finished : ca.nextDecidedByNames[c.nextDecidedBy]}
              </span>
              {c.visibility !== 'PRIVATE' && (
                <span className="chip">{ca.visibilityNames[c.visibility]}</span>
              )}
            </Link>
          ))}
        </section>
      )}
      {tab === 'missions' && (
        <>
          <div className="toolbar" role="group" aria-label={ca.libraryFilters.all}>
            {(['all', 'mine', 'shared', 'public'] as const).map((f) => (
              <button
                key={f}
                type="button"
                className={`chip toggle ${filter === f ? 'on' : ''}`}
                aria-pressed={filter === f}
                onClick={() => setFilter(f)}
              >
                {ca.libraryFilters[f]}
              </button>
            ))}
            {admin && (
              <button
                type="button"
                className={`chip toggle ${selecting ? 'on' : ''}`}
                aria-pressed={selecting}
                onClick={() => {
                  setSelecting(!selecting);
                  setPicked(new Set());
                }}
              >
                <i className="bi bi-check2-square" aria-hidden="true" />
                {selecting ? lib.selectDone : lib.select}
              </button>
            )}
            {admin && (
              <button
                type="button"
                className={`chip toggle ${tests ? 'on' : ''}`}
                aria-pressed={tests}
                title={lib.testsHint}
                onClick={() => setTests(!tests)}
              >
                <i className="bi bi-clipboard-check" aria-hidden="true" />
                {lib.tests}
              </button>
            )}
            <input
              className="control library-search"
              type="search"
              aria-label={ca.search}
              placeholder={ca.search}
              value={query}
              onChange={(e) => setQuery(e.target.value)}
            />
            <button
              type="button"
              className={`chip toggle ${filtersOpen || activeFilters > 0 ? 'on' : ''}`}
              aria-expanded={filtersOpen}
              aria-controls="library-filters"
              onClick={() => setFiltersOpen(!filtersOpen)}
            >
              <i className="bi bi-sliders" aria-hidden="true" />
              {activeFilters > 0 ? lib.filtersN(activeFilters) : lib.filtersLabel}
            </button>
            <label className="library-sort">
              {ca.missionUi.library.sort.label}
              <select
                className="control"
                value={sort}
                onChange={(e) => setSort(e.target.value as typeof sort)}
              >
                <option value="recent">{ca.missionUi.library.sort.recent}</option>
                <option value="mostPlayed">{ca.missionUi.library.sort.mostPlayed}</option>
                <option value="bestRated" disabled>
                  {ca.missionUi.library.sort.bestRated} ({ca.planned.chip})
                </option>
              </select>
            </label>
          </div>
          {filtersOpen && (
            <div className="filters" id="library-filters">
              <label>
                {ca.missionUi.library.filters.terrain}
                <select value={terrain} onChange={(e) => setTerrain(e.target.value)}>
                  <option value="">{ca.missionUi.library.allTerrains}</option>
                  {terrains.map(([world, label]) => (
                    <option key={world} value={world}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {ca.missionUi.library.filters.players}
                <select value={players} onChange={(e) => setPlayers(e.target.value)}>
                  <option value="">{ca.missionUi.library.anyPlayers}</option>
                  <option value="small">≤6</option>
                  <option value="medium">7–12</option>
                  <option value="large">13–24</option>
                  <option value="huge">25+</option>
                </select>
              </label>
              <label>
                {ca.missionUi.library.filters.duration}
                <select value={duration} onChange={(e) => setDuration(e.target.value)}>
                  <option value="">{ca.missionUi.library.anyDuration}</option>
                  {Object.entries(ca.missionUi.library.durations).map(([k, label]) => (
                    <option key={k} value={k}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {ca.missionUi.library.filters.recipe}
                <select value={recipe} onChange={(e) => setRecipe(e.target.value)}>
                  <option value="">{ca.missionUi.library.allRecipes}</option>
                  {recipes.map(([id, label]) => (
                    <option key={id} value={id}>
                      {label}
                    </option>
                  ))}
                </select>
              </label>
              <label>
                {ca.missionUi.library.filters.server}
                <select value={madeFor} onChange={(e) => setMadeFor(e.target.value)}>
                  <option value="">{ca.missionUi.library.allServers}</option>
                  {madeForNames.map((name) => (
                    <option key={name} value={name}>
                      {name}
                    </option>
                  ))}
                </select>
              </label>
              <label className="check">
                <input
                  type="checkbox"
                  checked={notPlayed}
                  onChange={(e) => setNotPlayed(e.target.checked)}
                />
                {ca.missionUi.library.filters.notPlayed}
              </label>
            </div>
          )}
        </>
      )}
      {tab === 'missions' && selecting && (
        <div className="toolbar sessions-bulk" role="group" aria-label={lib.select}>
          <button
            type="button"
            className="button small ghost"
            onClick={() => setPicked(new Set(shown.map((m) => m.code)))}
          >
            {lib.selectAll(shown.length)}
          </button>
          <button type="button" className="button small ghost" onClick={() => setPicked(new Set())}>
            {lib.selectNone}
          </button>
          <span className="grow" />
          {progress && (
            <span className="muted small num" role="status">
              {lib.purging(progress.done, progress.total)}
            </span>
          )}
          <button
            type="button"
            className="button small danger"
            disabled={pickedCodes.length === 0 || !!progress}
            onClick={() => void purgeMany(pickedCodes)}
          >
            <i className="bi bi-trash" aria-hidden="true" />
            {lib.purgeMany(pickedCodes.length)}
          </button>
        </div>
      )}
      {tab !== 'campaigns' && failures > 0 && (
        <p className="notice warn small">{lib.purgeFailed(failures)}</p>
      )}
      {tab === 'missions' && !loaded && (
        <section className="cards grid" aria-busy="true">
          <p className="muted" role="status">
            {ca.missionUi.library.loading}
          </p>
          {[0, 1, 2, 3, 4, 5].map((i) => (
            <div className="card mission skeleton" key={i} aria-hidden="true">
              <div className="thumb-wrap">
                <div className="thumb placeholder" />
              </div>
              <div className="body">
                <span className="line wide" />
                <span className="line" />
                <span className="line short" />
              </div>
            </div>
          ))}
        </section>
      )}
      {loadError !== null && <PageProblem error={loadError} onRetry={retry} />}
      {tab === 'missions' && loaded && shown.length === 0 && loadError === null && (
        <p className="muted">{ca.libraryEmpty}</p>
      )}
      {tab === 'missions' && loaded && (
        <section className="cards grid">
          {shown.map((m) => (
            <Link
              className={`card mission ${selecting && picked.has(m.code) ? 'picked' : ''}`}
              key={m.code}
              to={`/m/${m.code}`}
              aria-label={selecting ? lib.selectCard(m.title) : m.title}
              role={selecting ? 'checkbox' : undefined}
              aria-checked={selecting ? picked.has(m.code) : undefined}
              onClick={
                selecting
                  ? (e) => {
                      // While selecting, a card is a box to tick, not a way out of the page.
                      e.preventDefault();
                      setPicked((old) => {
                        const next = new Set(old);
                        if (next.has(m.code)) next.delete(m.code);
                        else next.add(m.code);
                        return next;
                      });
                    }
                  : undefined
              }
            >
              {selecting && (
                <span className="pick-box" aria-hidden="true">
                  <input type="checkbox" checked={picked.has(m.code)} readOnly tabIndex={-1} />
                </span>
              )}
              <div className="thumb-wrap">
                <MissionCover
                  code={m.code}
                  revision={m.revision}
                  world={m.worldName}
                  worldLabel={worldLabel(m)}
                  recipe={m.recipe}
                  recipeIcon={m.recipeIcon}
                  recipeTitle={typeLabel(m)}
                  cover={m.cover ?? 'AUTO'}
                />
                {m.recipe && (
                  <span className="chip">
                    <i className={`bi bi-${m.recipeIcon || 'flag'}`} aria-hidden="true" />{' '}
                    {typeLabel(m)}
                  </span>
                )}
                {isNew(m, now) && (
                  <span className="chip badge-new" title={lib.newHint}>
                    {lib.newTag}
                  </span>
                )}
              </div>
              <div className="body">
                <strong>{m.title}</strong>
                <span className="muted small">
                  {worldLabel(m)} · {m.playerSlots} jugadors
                </span>
                <span className="row author">
                  {m.ownerHandle && (
                    <>
                      <Avatar url={m.ownerAvatarUrl} name={m.ownerHandle} size={20} />
                      <span className="muted small">{ca.by(m.ownerHandle)}</span>
                    </>
                  )}
                  <span className="muted small">
                    {ca.missionUi.library.timesPlayed(m.sessions.length)}
                  </span>
                </span>
                {m.visibility !== 'PRIVATE' && (
                  <span className="chip">{ca.visibilityNames[m.visibility]}</span>
                )}
              </div>
            </Link>
          ))}
        </section>
      )}
      {confirmDialog}
    </Shell>
  );
}
